/**
 * API utility functions for fetching usage information
 */

const API_BASE_URL = process.env.REACT_APP_SERVER_API;

/**
 * Fetch usage information for all features
 * @returns {Promise<Object>} Usage data including is_limited flag and usage details
 */
export const fetchAllUsageInfo = async () => {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("Authentication token not found");
  }

  try {
    const response = await fetch(`${API_BASE_URL}/tariff/usage-info/`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching usage info:", error);
    throw error;
  }
};

/**
 * Fetch usage information for a specific feature
 * @param {string} featureType - Feature type (product_analytics, brand_analytics, etc.)
 * @returns {Promise<Object>} Usage data for the specific feature
 */
export const fetchFeatureUsageInfo = async (featureType) => {
  const token = localStorage.getItem("token");

  if (!token) {
    throw new Error("Authentication token not found");
  }

  try {
    const response = await fetch(
      `${API_BASE_URL}/tariffs/usage-info/${featureType}/`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error(`Error fetching ${featureType} usage info:`, error);
    throw error;
  }
};

/**
 * Check if a 403 error is due to usage limit exceeded
 * @param {Object} error - Error object from API response
 * @returns {boolean} True if error is usage limit exceeded
 */
export const isUsageLimitError = (error) => {
  if (!error || !error.detail) return false;

  const errorMessage = error.detail.toLowerCase();
  return (
    errorMessage.includes("превышен лимит") ||
    errorMessage.includes("usage_limit_exceeded")
  );
};

/**
 * Parse usage limit error message to extract details
 * @param {Object} error - Error object from API response
 * @returns {Object} Parsed error details
 */
export const parseUsageLimitError = (error) => {
  if (!error || !error.detail) {
    return { message: "Unknown error", current: 0, limit: 0 };
  }

  // Extract current/limit from message like "Использовано: 30/30"
  const match = error.detail.match(/(\d+)\/(\d+)/);

  return {
    message: error.detail,
    current: match ? parseInt(match[1], 10) : 0,
    limit: match ? parseInt(match[2], 10) : 0,
  };
};
